import axios from "axios";

export type ZendeskNoticeSeverity = "warning" | "error";

export interface ZendeskTicketLoadNotice {
  message: string;
  severity: ZendeskNoticeSeverity;
}

/**
 * Shown when a just-created ticket hasn't propagated to Zendesk's single-ticket
 * endpoint yet, so the request 404s (or comes back empty). The ticket already
 * appears in the list but its detail isn't ready; retrying after a moment
 * succeeds (KOA-17780).
 */
export const TICKET_NOT_READY_MESSAGE: string = "This ticket isn't ready yet. Please try again in a moment.";

/** Shown when opening a ticket fails for any reason other than a transient 404/empty response. */
export const TICKET_LOAD_FAILED_MESSAGE: string = "We couldn't open this ticket. Please try again.";

const TICKET_NOT_READY_NOTICE: ZendeskTicketLoadNotice = {
  message: TICKET_NOT_READY_MESSAGE,
  severity: "warning"
};

const TICKET_LOAD_FAILED_NOTICE: ZendeskTicketLoadNotice = {
  message: TICKET_LOAD_FAILED_MESSAGE,
  severity: "error"
};

/** Extracts the HTTP status from a caught value, when it is an Axios error; otherwise undefined. */
function getHttpStatus(error: unknown): number | undefined {
  if (axios.isAxiosError(error)) {
    return error.response?.status;
  }
  return undefined;
}

/**
 * Maps a ticket-detail fetch failure to a user-facing notice. A transient 404
 * (the ticket is still propagating to Zendesk) is treated as a soft, retryable
 * state with a gentle hint; anything else is a generic failure. In neither case
 * is the raw error/stack/response headers surfaced to the user.
 */
export function resolveTicketLoadNotice(error: unknown): ZendeskTicketLoadNotice {
  const status: number | undefined = getHttpStatus(error);
  if (status === 404) {
    return TICKET_NOT_READY_NOTICE;
  }
  return TICKET_LOAD_FAILED_NOTICE;
}
